/** The same rows as `TaskTable`, for a phone.
 *
 *  Every table in this app has fixed columns totalling 450-900px, so `.table-wrap`
 *  makes them scroll sideways on a 390px screen rather than truncate. That was the
 *  right fix for a table nobody could read; it is not a way to work a day. Reading
 *  seven columns by dragging left and right, then dragging back to find the row you
 *  were on, is the reason people were opening a laptop for something they could see
 *  in their hand.
 *
 *  So the same data, laid out down the screen instead of across it. Deliberately not
 *  a different set of facts: ref, status, task, client, who, and the one action that
 *  person can take on it. Anything more is a tap away in the drawer, which is where
 *  it already was.
 *
 *  **The headings are `TaskTable`'s own.** `groupRows` is exported from there and
 *  used verbatim, so the two views cannot group, count or sort differently - which
 *  they would within a month if each did its own.
 */
import { Fragment, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, type Task } from '@/api/client'
import { useAuth } from '@/lib/auth'
import { Caret, CollapseAll, groupKey, useCollapsed } from './Collapse'
import { Tick, type Selection } from './Selection'
import { approvalProgress, awaitingMyDecision } from '@/components/PeoplePicker'
import { fmtDate, fmtDay, MiniBar, StatusChip, Tag } from './ui'
import { groupRows, type GroupBy } from './TaskTable'

/** Whether this viewer is one of the people who has to do this task.
 *
 *  The same test `TaskDrawer` makes before it draws "Mark completed", and it lives
 *  here rather than being written twice: a card offering an action the drawer would
 *  not is a button that answers 403.
 */
export function isMine(t: Task, userId: string | undefined): boolean {
  if (!userId) return false
  return (t.people ?? []).some(
    (p) => p.kind === 'RESPONSIBLE' && p.user_id === userId)
}

/** The one thing this person can do to this task from a list, or nothing.
 *
 *  Only their own open work gets a button. An approver's decision is deliberately
 *  NOT here: approving needs the evidence in front of you, and the Approvals screen
 *  is where the attachments are. A one-tap Approve buried in a list is how work gets
 *  waved through.
 */
function CompleteButton({ task }: { task: Task }) {
  const qc = useQueryClient()
  const [err, setErr] = useState(false)
  const act = useMutation({
    mutationFn: () => api.post(`/tasks/${task.task_id}/complete`, {}),
    onSuccess: () => {
      setErr(false)
      qc.invalidateQueries({ queryKey: ['tasks'] })
      qc.invalidateQueries({ queryKey: ['day'] })
      qc.invalidateQueries({ queryKey: ['queue-count'] })
    },
    onError: () => setErr(true),
  })
  return (
    <button className="btn btn-good btn-sm shrink-0" disabled={act.isPending}
            onClick={(e) => { e.stopPropagation(); act.mutate() }}>
      {/* Not "Done": this sends the task to its approver and does not complete
          it. Every task has an approver now, so the old label was never true. */}
      {act.isPending ? 'Sending…' : err ? 'Try again' : 'Send'}
    </button>
  )
}

/** The same two priorities the table marks, in the same two colours. Regular is
 *  deliberately unmarked: a signal on every row is not a signal. */
const EDGE: Record<string, string> = {
  Urgent: 'border-l-[3px] border-l-rej',
  Important: 'border-l-[3px] border-l-pend',
}

function Card({ t, onOpen, showCompany }: {
  t: Task; onOpen: (t: Task) => void; showCompany: boolean
}) {
  const { me } = useAuth()
  const mine = isMine(t, me?.user_id)
  const terminal = t.status === 'COMPLETED' || t.status === 'CANCELLED'
  const canComplete = mine && !terminal && t.status !== 'PENDING_APPROVAL'
  const waiting = awaitingMyDecision(t, me?.user_id)
    && t.status === 'PENDING_APPROVAL'

  return (
    <button type="button" onClick={() => onOpen(t)}
            /* A real button, not a div with onClick: this is the whole row's tap
               target and it has to be reachable by keyboard and announced as
               actionable. `min-h-[68px]` keeps every card past the 44px thumb
               target even when the task text is one short line.
               The left edge carries priority, as the table's own `PriorityEdge`
               does - without it Urgent was invisible on a phone, which is the one
               screen where somebody is deciding what to do next. */
            className={`w-full text-left bg-surface border border-rule rounded-lg
                        px-3.5 py-3 min-h-[68px] active:bg-paper ${EDGE[t.priority]
                        ?? 'border-l'}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className="mono text-[12.5px] text-primary font-medium">#{t.ref}</span>
        <StatusChip status={t.status} approvalStatus={t.approval_status} />
        <span className="flex-1" />
        {t.status === 'REWORK' && <Tag tone="bad">rejected</Tag>}
        {t.carry_forward_count > 0 && <Tag>carried {t.carry_forward_count}×</Tag>}
        {/* Otherwise a task inside its window reads as an ordinary one that somehow
            escaped carry-forward - which is the question the tag answers. */}
        {t.end_date && <Tag>until {fmtDay(t.end_date)}</Tag>}
      </div>

      {/* The task text is what somebody is looking for, so it is the largest thing
          on the card and the only one that gets more than two lines. */}
      <p className="text-[14px] leading-snug">{t.task}</p>

      {/* The client only when the heading above is not already saying it - grouped
          by person it vanishes from the row entirely, and "for whom" is the first
          thing anybody asks about a task at an accountancy firm. */}
      {showCompany && t.company && (
        <p className="text-[12px] text-ink-3 mt-0.5">{t.company}</p>
      )}

      <div className="flex items-end gap-2 mt-2">
        <div className="flex-1 min-w-0 text-[12px] text-ink-3 leading-snug">
          <span className="block truncate">
            {t.responsible_person || 'Nobody yet'}
            {t.approver ? <> · approver {t.approver}</> : null}
          </span>
          {approvalProgress(t) && (
            <span className="block truncate">{approvalProgress(t)}</span>
          )}
          {t.remarks && (
            <span className="block mt-1 text-ink-2 line-clamp-2">{t.remarks}</span>
          )}
        </div>
        {canComplete ? <CompleteButton task={t} />
          : waiting ? <span className="mono text-[11px] text-pend-ink shrink-0">
                        yours to approve
                      </span>
          : null}
      </div>
    </button>
  )
}

export function TaskCards({ tasks, onOpen, footer, groupByDate = false,
                            groupBy = 'company', selection,
                            startFolded = false }: {
  tasks: Task[]; onOpen: (t: Task) => void; footer?: string
  groupByDate?: boolean
  groupBy?: GroupBy
  selection?: Selection
  /** Open with the groups folded. Worth more here than on a laptop: a hundred
   *  headings is a hundred screenfuls on a phone. See `Collapse`. */
  startFolded?: boolean
}) {
  const days = groupRows(tasks, { groupByDate, groupBy })
  const collapsed = useCollapsed(startFolded)
  const allKeys = days.flatMap(
    (d) => d.groups.map((g) => groupKey(d.date, g.company)))
  return (
    <div className="space-y-4">
      {allKeys.length > 1 && (
        <div className="flex justify-end">
          <CollapseAll collapsed={collapsed} keys={allKeys} />
        </div>
      )}
      {days.map((d) => {
        const dayKey = groupKey(d.date, '')
        const dayShut = groupByDate && collapsed.has(dayKey)
        return (
        <Fragment key={d.date || 'all'}>
          {groupByDate && (
            <button type="button" onClick={() => collapsed.toggle(dayKey)}
                    aria-expanded={!dayShut}
                    className="w-full text-left flex items-center gap-1.5 pt-1">
              <Caret open={!dayShut} />
              <span className="text-[13px] font-semibold tracking-tight">
                {d.date ? fmtDate(d.date) : 'No date'}
              </span>
              <MiniBar c={d.counts} />
            </button>
          )}
          {!dayShut && d.groups.map((g) => {
            const shut = collapsed.has(groupKey(d.date, g.company))
            return (
            <div key={g.company} className="space-y-2">
              {/* Sticky, because on a phone the heading scrolls away after three
                  cards and "which client is this?" is then unanswerable without
                  scrolling back up. `top-0` works because the page scroller is
                  `main`, not the window. */}
              <button type="button"
                      onClick={() => collapsed.toggle(groupKey(d.date, g.company))}
                      aria-expanded={!shut}
                      className="w-full text-left sticky top-0 z-[1] -mx-3.5 px-3.5
                                 py-1.5 bg-paper flex items-center gap-2">
                <Caret open={!shut} />
                <span className="text-[12.5px] font-semibold tracking-tight">
                  {g.company}
                </span>
                <MiniBar c={g.counts} />
                <span className="mono text-[11.5px] text-ink-3">
                  {g.rows.length}
                </span>
              </button>
              {!shut && g.rows.map((t, i) => {
                const key = t.task_id || `pending-${g.company}-${i}`
                const card = (
                  <Card t={t} onOpen={onOpen}
                        showCompany={groupBy === 'person'} />
                )
                // The card IS a button - the whole row is the tap target - so the
                // checkbox cannot live inside it: a checkbox nested in a button is
                // invalid and unreachable independently. It sits beside it instead.
                return selection ? (
                  <div key={key} className="flex items-start gap-2.5">
                    <span className="pt-4 shrink-0">
                      <Tick selection={selection} task={t} />
                    </span>
                    <span className="flex-1 min-w-0">{card}</span>
                  </div>
                ) : <Fragment key={key}>{card}</Fragment>
              })}
            </div>
            )
          })}
        </Fragment>
        )
      })}
      {footer && <p className="text-xs text-ink-3 px-1 pt-1">{footer}</p>}
    </div>
  )
}
